import { useId, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isApiError } from '../api/client';
import { filmStocks, isDuplicateStockBody, type FilmStock, type FilmStockInput } from '../api/filmStocks';
import { queryKeys } from '../api/queryKeys';
import { FilmStockForm } from '../components/FilmStockForm';
import { FilmStockPicker } from '../components/FilmStockPicker';
import { ModalDialog } from '../components/ModalDialog';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminCall } from '../hooks/useAdminCall';
import { usePageTitle } from '../hooks/usePageTitle';
import { brandsOf, PROCESS_LABELS, sortStocks, splitNewStockQuery, stockLabel } from '../utils/filmStocks';
import styles from './AdminFilmStocksPage.module.css';

type Editing = { stock: FilmStock } | { initial: Partial<FilmStockInput> } | null;

/**
 * The film stocks technicians pick from when booking rolls in: find, add and
 * edit them, turn them off, and load the starter list.
 */
export function AdminFilmStocksPage() {
  usePageTitle('Admin — Film stocks');
  const { logout } = useAdminAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Editing>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<FilmStock | null>(null);
  const [showOff, setShowOff] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const captionId = useId();

  const call = useAdminCall();

  const { data: stocks, isLoading, error } = useQuery({
    queryKey: queryKeys.admin.filmStocks(),
    queryFn: () => call(filmStocks.list),
  });

  const all = useMemo(() => sortStocks(stocks ?? []), [stocks]);
  const brands = useMemo(() => brandsOf(all), [all]);
  const offCount = all.filter((s) => !s.active).length;
  const shown = showOff ? all : all.filter((s) => s.active);

  function open(next: Editing) {
    setFormError(null);
    setDuplicate(null);
    setEditing(next);
  }

  const save = useMutation({
    mutationFn: ({ stock, input }: { stock?: FilmStock; input: FilmStockInput }) =>
      call(() => (stock ? filmStocks.update(stock.stock_id, input) : filmStocks.create(input))),
    onSuccess: (saved, { stock }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.filmStocks() });
      setEditing(null);
      setAnnouncement(`${stock ? 'Saved' : 'Added'} ${stockLabel(saved)}.`);
    },
    onError: (e) => {
      const body = isApiError(e, 409) ? e.body : null;
      setDuplicate(isDuplicateStockBody(body) ? (all.find((s) => s.stock_id === body.stock_id) ?? null) : null);
      setFormError((isApiError(e) && e.serverMessage) || 'Saving failed. Try again.');
    },
  });

  const seed = useMutation({
    mutationFn: () => call(filmStocks.seed),
    onSuccess: ({ added, refreshed }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.filmStocks() });
      const plural = (n: number) => `${n} ${n === 1 ? 'stock' : 'stocks'}`;
      setAnnouncement(
        refreshed === 0
          ? `Added ${plural(added)}.`
          : `Added ${plural(added)}; ${plural(refreshed)} were already here and kept your changes.`,
      );
    },
    onError: (e) => setAnnouncement((isApiError(e) && e.serverMessage) || 'Loading the starter list failed. Try again.'),
  });

  const seedButton = (label: string, primary: boolean) => (
    <button
      type="button"
      className={primary ? styles.primaryBtn : styles.secondaryBtn}
      onClick={() => seed.mutate()}
      disabled={seed.isPending}
    >
      {seed.isPending ? 'Loading…' : label}
    </button>
  );

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.heading}>Admin — Film Stocks</h1>
        <div className={styles.headerActions}>
          <Link to="/admin/orders" className={styles.secondaryBtn}>
            Orders
          </Link>
          <Link to="/admin/print-orders" className={styles.secondaryBtn}>
            Print Orders
          </Link>
          <Link to="/admin/lab" className={styles.secondaryBtn}>
            Lab
          </Link>
          <button type="button" onClick={logout} className={styles.secondaryBtn}>
            Logout
          </button>
        </div>
      </div>

      <p className={styles.intro}>
        The stocks technicians pick from when booking rolls in. A stock can't be deleted, because rolls point at it:
        turn it off to take it out of the picker.
      </p>

      <div role="status" aria-live="polite" className={styles.announcement}>
        {announcement}
      </div>

      {isLoading && <p className={styles.loading}>Loading film stocks…</p>}
      {error && <p className={styles.error}>Error loading film stocks.</p>}

      {stocks && stocks.length === 0 && (
        <section className={styles.emptyState} aria-labelledby={`${captionId}-empty`}>
          <h2 id={`${captionId}-empty`}>No film stocks yet</h2>
          <p>
            Start with the starter list: about 300 current and recent 35mm and 120 films, from the shop, Wikipedia and
            the Open Source Film Database. You can add and change stocks afterwards.
          </p>
          {seedButton('Add the starter list', true)}
        </section>
      )}

      {stocks && stocks.length > 0 && (
        <>
          <div className={styles.toolbar}>
            <div className={styles.finder}>
              <FilmStockPicker
                label="Find a stock"
                hint="Type a brand, name or speed. Pick one to edit it."
                stocks={all}
                clearOnPick
                onPick={(stock) => open({ stock })}
                onAddNew={(query) => open({ initial: splitNewStockQuery(query, brands) })}
              />
            </div>
            <button type="button" className={styles.primaryBtn} onClick={() => open({ initial: {} })}>
              Add a stock
            </button>
          </div>

          {offCount > 0 && (
            <label className={styles.showOff}>
              <input type="checkbox" checked={showOff} onChange={(e) => setShowOff(e.target.checked)} />
              Show turned-off stocks ({offCount})
            </label>
          )}

          {/* Focusable so a keyboard can scroll it sideways on a narrow screen. */}
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
          <div className={styles.tableWrapper} tabIndex={0} role="region" aria-labelledby={captionId}>
            <table className={styles.table}>
              <caption id={captionId} className={styles.caption}>
                {shown.length} film stocks, by brand
              </caption>
              <thead>
                <tr>
                  <th scope="col">Brand</th>
                  <th scope="col">Name</th>
                  <th scope="col">ISO</th>
                  <th scope="col">Process</th>
                  <th scope="col">Formats</th>
                  <th scope="col">In the shop</th>
                  <th scope="col">Other names</th>
                  <th scope="col">
                    <span className={styles.srOnly}>Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((s) => (
                  <tr key={s.stock_id} className={s.active ? undefined : styles.off}>
                    <td>{s.brand}</td>
                    <td>
                      {s.name}
                      {!s.active && <span className={styles.offBadge}>Off</span>}
                    </td>
                    <td>{s.box_iso ?? '—'}</td>
                    <td>{PROCESS_LABELS[s.process] ?? s.process}</td>
                    <td>{s.formats.join(', ') || '—'}</td>
                    <td>{s.shop_products.map((p) => p.format).join(', ') || '—'}</td>
                    <td className={styles.aliases}>{s.aliases.join(', ')}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.secondaryBtn}
                        onClick={() => open({ stock: s })}
                        aria-label={`Edit ${stockLabel(s)}`}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <section className={styles.starter} aria-labelledby={`${captionId}-starter`}>
            <h2 id={`${captionId}-starter`}>Starter list</h2>
            <p>
              Loading it again adds any stocks that are missing and updates which ones the shop sells. It never changes
              a stock you've edited or turned off.
            </p>
            {seedButton('Load the starter list again', false)}
          </section>
        </>
      )}

      <p className={styles.credits}>
        The starter list uses data from Wikipedia’s{' '}
        <a href="https://en.wikipedia.org/wiki/List_of_photographic_films" target="_blank" rel="noopener noreferrer">
          List of photographic films
        </a>{' '}
        and the{' '}
        <a href="https://github.com/dxdatabase/Open-source-film-database" target="_blank" rel="noopener noreferrer">
          Open Source Film Database
        </a>
        , both{' '}
        <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener noreferrer">
          CC BY-SA 4.0
        </a>
        .
      </p>

      {editing && (
        <ModalDialog
          title={'stock' in editing ? `Edit ${stockLabel(editing.stock)}` : 'Add a film stock'}
          onClose={() => setEditing(null)}
        >
          <FilmStockForm
            key={'stock' in editing ? editing.stock.stock_id : 'new'}
            stock={'stock' in editing ? editing.stock : undefined}
            initial={'initial' in editing ? editing.initial : undefined}
            brands={brands}
            saving={save.isPending}
            error={formError}
            duplicate={duplicate}
            onSubmit={(input) => save.mutate({ stock: 'stock' in editing ? editing.stock : undefined, input })}
            onCancel={() => setEditing(null)}
            onOpenDuplicate={(stock) => open({ stock })}
          />
        </ModalDialog>
      )}
    </main>
  );
}
